


import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { 
    onAuthStateChanged,
    signInWithPopup,
    signOut,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    User,
    UserCredential,
    AuthError as FirebaseError, // Using AuthError as the type for Firebase errors in v9
    signInAnonymously
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    signInWithGoogle: () => Promise<void>;
    signOutUser: () => Promise<void>;
    signUpWithEmail: (email: string, password: string) => Promise<UserCredential | string>;
    signInWithEmail: (email: string, password: string) => Promise<UserCredential | string>;
    resetPassword: (email: string) => Promise<void | string>;
    signInAnonymouslyIfNeeded: () => Promise<User | null>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getFirebaseErrorMessage = (error: FirebaseError): string => {
    switch (error.code) {
        case 'auth/email-already-in-use':
            return 'Este correo electrónico ya está registrado.';
        case 'auth/invalid-email':
            return 'El formato del correo electrónico no es válido.';
        case 'auth/weak-password':
            return 'La contraseña debe tener al menos 6 caracteres.';
        case 'auth/user-not-found':
            return 'No se encontró una cuenta con este correo electrónico.';
        case 'auth/wrong-password':
            return 'La contraseña es incorrecta. Inténtalo de nuevo.';
        case 'auth/invalid-credential':
             return 'Las credenciales son incorrectas. Verifica tu correo y contraseña.';
        default:
            console.error('Firebase Auth Error:', error);
            return 'Ocurrió un error inesperado. Por favor, inténtalo de nuevo.';
    }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const signInAnonymouslyIfNeeded = async (): Promise<User | null> => {
        if (auth.currentUser) {
            return auth.currentUser;
        }
        try {
            const userCredential = await signInAnonymously(auth);
            return userCredential.user;
        } catch (error) {
            console.error("Anonymous sign-in failed", error);
            return null;
        }
    };

    const signInWithGoogle = async () => {
        try {
            await signInWithPopup(auth, googleProvider);
        } catch (error) {
            console.error("Error signing in with Google: ", error);
        }
    }; 

    const signOutUser = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error("Error signing out: ", error);
        }
    };
    
    const signUpWithEmail = async (email: string, password: string): Promise<UserCredential | string> => {
        try {
            return await createUserWithEmailAndPassword(auth, email, password);
        } catch (error) {
            return getFirebaseErrorMessage(error as FirebaseError);
        }
    };

    const signInWithEmail = async (email: string, password: string): Promise<UserCredential | string> => {
        try {
            return await signInWithEmailAndPassword(auth, email, password);
        } catch (error) {
            return getFirebaseErrorMessage(error as FirebaseError);
        }
    };

    const resetPassword = async (email: string): Promise<void | string> => {
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (error) {
            return getFirebaseErrorMessage(error as FirebaseError);
        }
    };

    const value = { user, loading, signInWithGoogle, signOutUser, signUpWithEmail, signInWithEmail, resetPassword, signInAnonymouslyIfNeeded };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};